import Analytics from '@withlocals/withlocals-analytics';
import classnames from 'classnames';
import PlayIcon from 'components/icons/PlayIcon';
import React, { CSSProperties, PureComponent, ReactElement } from 'react';

import { WlWindow } from '../../../typings/globals/cordova';
import FullscreenButton from './FullscreenButton';
import MuteButton from './MuteButton';

declare const window: WlWindow;

const CSS = require('./Video.module.scss');

const getVideoStyle = ({
  width,
  height,
}: {
  width?: number;
  height?: number;
}) => ({
  width,
  height,
});

interface FsDocument extends HTMLDocument {
  mozFullScreenElement?: Element;
  msFullscreenElement?: Element;
  webkitFullscreenElement?: Element;
  msExitFullscreen?: () => void;
  mozCancelFullScreen?: () => void;
}

export interface VideoState {
  playing: boolean;
  ended: boolean;
  clicked?: boolean;
  muted?: boolean;
  fullscreen: boolean;
}

interface VideoProps {
  videoName: string;
  play?: boolean;
  autoplay?: boolean;
  pause?: boolean;
  onPlay?: (state: VideoState) => void;
  onPause?: (state: VideoState) => void;
  onFullScreen?: (event: any, isFullscreen: boolean) => void;
  onClick?: (e: any) => void;
  onEnded?: (e: any) => void;
  onLoadedMetadata?: (e: any) => void;
  poster?: string;
  width?: number;
  height?: number;
  className?: string;
  videoClassName?: string;
  playIconClassName?: string;
  controls?: boolean;
  style?: CSSProperties;
  playsInline?: boolean;
  controlsList?: string;
  displayIcons?: boolean;
  loop?: boolean;
  muted?: boolean;
  muteClassName?: string;
  tryFetchSubtitles?: boolean;
}

const getSubtitleUrlFromVideoUrl = (videoUrl: string) => {
  const subtitleUrl = videoUrl
    .replace('/video/', '/raw/')
    .replace(new RegExp('/v[0-9]{10}/'), '/')
    .replace('.mp4', '.vtt')
    .replace('.webm', '.vtt');

  if (!subtitleUrl.endsWith('.vtt')) {
    return `${subtitleUrl}.vtt`;
  }

  return subtitleUrl;
};

class Video extends PureComponent<VideoProps, VideoState> {
  videoRef: HTMLVideoElement | null = null;

  state: VideoState = {
    playing: false,
    ended: false,
    muted: this.props.muted,
    fullscreen: false,
  };

  componentDidMount(): void {
    if (this.videoRef) {
      this.videoRef.addEventListener(
        'webkitfullscreenchange',
        this.onFullScreen,
      );
      this.videoRef.addEventListener('fullscreenchange', this.onFullScreen);
      this.videoRef.addEventListener('mozfullscreenchange', this.onFullScreen);
      this.videoRef.addEventListener('MSFullscreenChange', this.onFullScreen);
    }
  }

  componentWillUnmount(): void {
    if (this.videoRef) {
      this.videoRef.removeEventListener(
        'webkitfullscreenchange',
        this.onFullScreen,
      );
      this.videoRef.removeEventListener('fullscreenchange', this.onFullScreen);
      this.videoRef.removeEventListener(
        'mozfullscreenchange',
        this.onFullScreen,
      );
      this.videoRef.removeEventListener(
        'MSFullscreenChange',
        this.onFullScreen,
      );
    }
  }

  componentDidUpdate(
    prevProps: Readonly<VideoProps>,
    prevState: Readonly<VideoState>,
  ): void {
    if (this.props.play && this.props.autoplay && !prevState.playing) {
      this.videoRef?.play();
    } else if (this.props.pause && prevState.playing) {
      this.videoRef?.pause();
    }
  }

  onFullScreen = (e: Event) => {
    const fsDocument: FsDocument = document;
    const fullscreen =
      !!fsDocument.fullscreenElement ||
      !!fsDocument.mozFullScreenElement ||
      !!fsDocument.webkitFullscreenElement ||
      !!fsDocument.msFullscreenElement;
    const current = e.currentTarget as HTMLVideoElement | null;
    const time = Math.round(current ? current.currentTime : 0);

    if (fullscreen) {
      Analytics.track(
        window.currentPageCategory,
        `Video [${this.props.videoName}] fullscreen open`,
        time,
      );
    } else {
      Analytics.track(
        window.currentPageCategory,
        `Video [${this.props.videoName}] fullscreen close`,
        time,
      );
    }

    if (this.props.onFullScreen) {
      this.props.onFullScreen(e, fullscreen);
    }
  };

  setVideoRef = (ref: HTMLVideoElement | null) => {
    this.videoRef = ref;
  };

  onPlay = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    Analytics.track(
      window.currentPageCategory,
      `Video [${this.props.videoName}] play`,
      Math.round(e.currentTarget.currentTime),
    );
    this.setState({ playing: true }, () => {
      if (this.props.onPlay) {
        this.props.onPlay(this.state);
      }
    });
  };

  onPause = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    Analytics.track(
      window.currentPageCategory,
      `Video [${this.props.videoName}] pause`,
      Math.round(e.currentTarget.currentTime),
    );
    this.setState({ playing: false }, () => {
      if (this.props.onPause) {
        this.props.onPause(this.state);
      }
    });
  };

  onEnded = () => {
    Analytics.track(
      window.currentPageCategory,
      `Video [${this.props.videoName}] ended`,
      'Video ended',
    );

    this.videoRef?.load();
    this.setState({ playing: false }, () => {
      if (this.props.onEnded) {
        this.props.onEnded(this.state);
      }
    });
  };

  onContainerClick = () => {
    const nowPlaying = this.state.playing;
    this.setState({ clicked: true, playing: !nowPlaying }, () => {
      if (nowPlaying) {
        this.videoRef?.pause();
      } else {
        this.videoRef?.play();
      }
      if (this.props.onClick) {
        this.props.onClick(this.state);
      }
      this.setState({ clicked: false });
    });
  };

  toggleMuted = (event: React.MouseEvent) => {
    event.stopPropagation();
    this.setState({
      muted: !this.state.muted,
    });
  };

  toggleFullscren = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!this.videoRef) return;

    interface ExtendedVideoElement extends HTMLVideoElement {
      webkitRequestFullscreen?: () => void;
      mozRequestFullScreen?: () => void;
      msRequestFullscreen?: () => void;
    }

    const el: ExtendedVideoElement = this.videoRef as ExtendedVideoElement;
    const request =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;

    if (request) {
      request.call(el);
    }

    this.setState({
      fullscreen: !this.state.fullscreen,
    });
  };

  getSubtitleTrack = () => {
    let src = '';
    React.Children.forEach(this.props.children, (child) => {
      const el = child as ReactElement<any> | null;
      if (!src && el && el.type === 'source') {
        // source elements should have a string src
        const childSrc = (el.props as { src?: string }).src;
        if (typeof childSrc === 'string') {
          src = childSrc;
        }
      }
    });

    if (!src) {
      return null;
    }

    return (
      <track
        label="English"
        kind="subtitles"
        srcLang="en"
        src={getSubtitleUrlFromVideoUrl(src)}
        default
      />
    );
  };

  render() {
    const {
      autoplay,
      poster,
      width,
      height,
      children,
      className,
      videoClassName,
      playIconClassName,
      controls,
      style = getVideoStyle({ width, height }),
      playsInline = true,
      controlsList = '',
      displayIcons = true,
      muteClassName,
    } = this.props;

    return (
      <div
        onClick={this.onContainerClick}
        className={classnames(CSS.videoContainer, className)}
      >
        {displayIcons && !controls && (
          <>
            <PlayIcon
              className={classnames(CSS.playIcon, playIconClassName, {
                [CSS.playIconHidden]: this.state.playing,
              })}
              width={50}
              height={50}
            />
            {this.state.playing && typeof this.state.muted === 'boolean' && (
              <>
                <MuteButton
                  muted={this.state.muted}
                  onClick={this.toggleMuted}
                  className={classnames(CSS.muteButton, muteClassName)}
                />
                <FullscreenButton
                  className={CSS.fullscreenButton}
                  fullscreen={this.state.fullscreen}
                  onClick={this.toggleFullscren}
                />
              </>
            )}
          </>
        )}
        <video
          crossOrigin={this.props.tryFetchSubtitles ? 'anonymous' : ''}
          loop={this.props.loop}
          controls={controls}
          className={classnames(CSS.video, videoClassName)}
          playsInline={playsInline}
          onEnded={this.onEnded}
          onPlay={this.onPlay}
          onPause={this.onPause}
          autoPlay={autoplay}
          ref={this.setVideoRef}
          poster={poster}
          preload={autoplay ? 'auto' : 'metadata'}
          width={width}
          height={height}
          style={style}
          controlsList={controlsList}
          muted={!!this.state.muted}
          onLoadedMetadata={this.props.onLoadedMetadata}
        >
          {children}
          {this.props.tryFetchSubtitles && this.getSubtitleTrack()}
        </video>
      </div>
    );
  }
}

export default Video;
